import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'react-feather';

/**
 * Generic V3 multi-select with search, checkboxes and chips. Replaces the
 * inline-styled tenant dropdown copy-pasted across every report.
 *
 * The menu is portalled to document.body with fixed positioning so it
 * escapes any ancestor card's `overflow:hidden` + `backdrop-filter`
 * stacking context (same pattern as Tenant/shared/DeliveryPartnersField).
 *
 * Props:
 *   value        — array of selected option values (ids)
 *   options      — [{ value, label, sub? }]
 *   onChange     — (nextValueArray) => void
 *   placeholder  — toggle text when nothing selected
 *   searchable   — show search box (default true)
 *   maxChips     — chips before collapsing to "+N more" (default 3)
 */
const MultiSelect = ({
    value = [],
    options = [],
    onChange,
    placeholder = 'Select…',
    searchable = true,
    maxChips = 3,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const [search, setSearch] = useState('');
    const wrapRef = useRef(null);
    const toggleRef = useRef(null);
    const menuRef = useRef(null);

    const updateRect = useCallback(() => {
        const el = toggleRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.bottom + 4, width: r.width });
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        updateRect();
        const onReflow = () => updateRect();
        window.addEventListener('scroll', onReflow, true);
        window.addEventListener('resize', onReflow);
        return () => {
            window.removeEventListener('scroll', onReflow, true);
            window.removeEventListener('resize', onReflow);
        };
    }, [open, updateRect]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const selected = useMemo(
        () => options.filter((o) => value.includes(o.value)),
        [options, value],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) =>
                (o.label || '').toLowerCase().includes(q) ||
                (o.sub || '').toLowerCase().includes(q),
        );
    }, [options, search]);

    const toggle = (val) => {
        if (value.includes(val)) onChange?.(value.filter((v) => v !== val));
        else onChange?.([...value, val]);
    };

    const removeChip = (e, val) => {
        e.stopPropagation();
        onChange?.(value.filter((v) => v !== val));
    };

    const menu = open && rect
        ? createPortal(
            <div
                ref={menuRef}
                className="v3-sadmin-multi-menu"
                style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, right: 'auto', zIndex: 1000 }}
            >
                {searchable && (
                    <div className="v3-sadmin-search" style={{ width: '100%', marginBottom: 6 }}>
                        <Search size={14} />
                        <input
                            autoFocus
                            type="text"
                            placeholder={t('Search…')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                )}
                {filtered.length === 0 ? (
                    <div className="v3-sadmin-multi-item" style={{ color: 'var(--v3-text-muted)' }}>
                        {t('No results')}
                    </div>
                ) : (
                    filtered.map((o) => (
                        <label key={o.value} className="v3-sadmin-multi-item">
                            <input
                                type="checkbox"
                                checked={value.includes(o.value)}
                                onChange={() => toggle(o.value)}
                            />
                            <span>
                                {o.label}
                                {o.sub ? <span style={{ color: 'var(--v3-text-muted)' }}> — {o.sub}</span> : null}
                            </span>
                        </label>
                    ))
                )}
            </div>,
            document.body,
        )
        : null;

    return (
        <div className="v3-sadmin-multi" ref={wrapRef}>
            <button
                type="button"
                ref={toggleRef}
                className={`v3-sadmin-multi-toggle ${open ? 'open' : ''}`}
                onClick={() => setOpen((o) => !o)}
            >
                {selected.length === 0 ? (
                    <span className="placeholder">{t(placeholder)}</span>
                ) : (
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                        {selected.slice(0, maxChips).map((s) => (
                            <span key={s.value} className="v3-report-chip">
                                {s.label}
                                <X size={11} onClick={(e) => removeChip(e, s.value)} />
                            </span>
                        ))}
                        {selected.length > maxChips && (
                            <span className="v3-sadmin-pill off">{t('+{{count}} more', { count: selected.length - maxChips })}</span>
                        )}
                    </span>
                )}
                <ChevronDown size={14} />
            </button>
            {menu}
        </div>
    );
};

export default MultiSelect;
